import type { BiomeId } from '@/styles/theme'

export type WorldPropKind =
  | 'forest-tree'
  | 'fern'
  | 'root'
  | 'mushroom'
  | 'cave-stalagmite'
  | 'cave-crystal'
  | 'mineral-pool'
  | 'lava-vent'
  | 'ash-rock'
  | 'ember-crack'
  | 'ice-pine'
  | 'ice-spire'
  | 'snow-drift'
  | 'rune-stone'
  | 'star-flower'
  | 'floating-shard'
  | 'torch'
  | 'chain-post'
  | 'gate-fragment'
  | 'stone-block'

export interface WorldLifeLayout {
  rows: number
  cols: number
  extent: number
}

export type GraphicsQuality = 'high' | 'medium' | 'low'

export interface WorldPropPlacement {
  x: number
  z: number
  scale: number
  rot: number
  kind: WorldPropKind
  tone: number
}

export interface WorldNpcPlacement {
  base: [number, number]
  color: string
  key: number
}

export interface WorldLifePlacement {
  props: WorldPropPlacement[]
  npcs: WorldNpcPlacement[]
  moteCount: number
}

function seeded(seed: number) {
  let s = seed >>> 0 || 1
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 0xffffffff)
}

export function propKindsForBiome(biome: BiomeId): WorldPropKind[] {
  if (biome === 'forest') return ['forest-tree', 'fern', 'root', 'mushroom', 'forest-tree']
  if (biome === 'cave') return ['cave-stalagmite', 'cave-crystal', 'mineral-pool', 'stone-block']
  if (biome === 'lava') return ['lava-vent', 'ash-rock', 'ember-crack', 'stone-block']
  if (biome === 'ice') return ['ice-pine', 'ice-spire', 'snow-drift', 'ice-pine']
  if (biome === 'magical') return ['rune-stone', 'star-flower', 'floating-shard', 'cave-crystal']
  return ['torch', 'chain-post', 'gate-fragment', 'stone-block']
}

export function worldPropCount(graphics: GraphicsQuality): number {
  if (graphics === 'low') return 0
  if (graphics === 'medium') return 5
  return 10
}

export function worldNpcCount(graphics: GraphicsQuality): number {
  if (graphics === 'low') return 0
  if (graphics === 'medium') return 1
  return 2
}

export function worldMoteCount(graphics: GraphicsQuality): number {
  if (graphics === 'low') return 0
  if (graphics === 'medium') return 8
  return 24
}

export function buildWorldLife(layout: WorldLifeLayout, biome: BiomeId, graphics: GraphicsQuality, emberColor: string): WorldLifePlacement {
  const propRnd = seeded(layout.cols * 131 + layout.rows * 17 + 7)
  const half = layout.extent / 2
  const ring = half + 0.52
  const kinds = propKindsForBiome(biome)
  const props: WorldPropPlacement[] = []
  const propCount = worldPropCount(graphics)
  const frontBudget = graphics === 'high' ? 1 : 0

  let frontCount = 0
  for (let attempt = 0; props.length < propCount && attempt < propCount * 4; attempt++) {
    const ang = (attempt / propCount) * Math.PI * 2 + propRnd() * 0.5
    const front = Math.sin(ang) > 0.2
    if (front && frontCount >= frontBudget) continue
    if (front) frontCount += 1
    const rear = Math.sin(ang) < -0.28
    const rad = ring + propRnd() * (half * 0.24) + (front ? half * 0.48 : rear ? half * 0.08 : half * 0.18)
    const baseScale = 0.7 + propRnd() * 0.82
    props.push({
      x: Math.cos(ang) * rad,
      z: Math.sin(ang) * rad,
      scale: Math.max(front ? 0.3 : 0.62, baseScale * (front ? 0.36 : rear ? 1.02 : 0.78)),
      rot: propRnd() * Math.PI * 2,
      kind: kinds[(propRnd() * kinds.length) | 0],
      tone: propRnd(),
    })
  }

  const npcRnd = seeded(layout.cols * 91 + layout.rows * 53 + 3)
  const npcHalf = layout.extent / 2 + 1.1
  const palette = ['#4a90e2', '#4caf50', '#9c27b0', emberColor]
  const npcs = Array.from({ length: worldNpcCount(graphics) }, (_, i) => ({
    base: [(npcRnd() - 0.5) * 2 * npcHalf, (npcRnd() > 0.5 ? 1 : -1) * (npcHalf - npcRnd() * 0.6)] as [number, number],
    color: palette[(npcRnd() * palette.length) | 0],
    key: i,
  }))

  return {
    props,
    npcs,
    moteCount: worldMoteCount(graphics),
  }
}
